import api from './api'

// Types for Module and Exam data
export interface Module {
  module_id: number
  name: string
  description: string
  status: number
  status_name: string
  available_months: Array<{
    number: number
    name: string
  }>
  time_settings: Array<{
    start_date: string
    end_date: string
    cycle_year: number
    month: number
    month_name: string
  }>
  rotation_cycles: number
}

export interface Exam {
  exam_id: number
  name: string
  passing_rate: number
  time_limit: number
  time_limit_formatted: string
  total_attempts: number
  status: number
  status_name: string
  created_at: string
  updated_at: string
}

export interface ModuleAndExam {
  module: Module
  exam: Exam | null
}

export interface EmployeeAssignments {
  year: number
  assigned_months: Array<{
    number: number
    name: string
  }>
  modules_and_exams: ModuleAndExam[]
  summary: {
    total_modules: number
    modules_with_exams: number
    modules_without_exams: number
  }
}

export interface CurrentMonthModule {
  module_id: number
  module_name: string
  module_description: string
  module_status: number
  time_setting: {
    start_date: string
    end_date: string
  } | null
  exam: Exam | null
}

export interface MyAssignments {
  assignment_id: number
  year: number
  assigned_months: number[]
  assigned_months_names: Array<{
    number: number
    name: string
  }>
  available_modules: Array<{
    module_id: number
    module_name: string
    module_description: string
    module_status: number
    available_months: Array<{
      number: number
      name: string
    }>
    time_settings: Array<{
      start_date: string
      end_date: string
      cycle_year: number
      month: number
      month_name: string
    }>
    exam: Exam | null
  }>
  current_month_modules: CurrentMonthModule[]
  current_month: number
  has_current_month_access: boolean
}

export interface ExamResult {
  exam_result_id: number
  user_score: number
  total_score: number
  passing_rate: number
  is_passed: boolean
  num_of_attempts: number
  date_taken: string
}

export interface AccessibleModule {
  module_id: number
  name: string
  description: string
  status: number
  access_type: 'current_month' | 'previously_accessible'
  completion_status?: 'completed_passed' | 'completed_failed' | 'not_attempted' | 'no_exam_required'
  completed_date?: string | null
  available_in_months?: Array<{
    number: number
    name: string
  }>
  time_settings?: Array<{
    start_date: string
    end_date: string
    cycle_year: number
  }>
  exam: {
    exam_id: number
    name: string
    passing_rate: number
    time_limit: number | null
    time_limit_formatted: string | null
    total_attempts: number
    status: number
  } | null
  exam_result: ExamResult | null
}

export interface AccessibleModules {
  year: number
  current_month: number
  has_current_month_access: boolean
  current_month_modules: AccessibleModule[]
  previous_modules: AccessibleModule[]
  accessible_modules_count: number
  summary: {
    current_month_modules: number
    previous_modules: number
  }
}

export interface ApiResponse<T> {
  success: boolean
  message: string
  data: T
}

export const employeeAssignmentApi = {
  /**
   * Get employee's modules and exams in a clean format
   */
  getMyModulesAndExams: async (year?: number): Promise<EmployeeAssignments> => {
    try {
      const params = year ? { year } : {}
      const response = await api.get<ApiResponse<EmployeeAssignments>>('/my-modules-and-exams', { params })
      return response.data.data
    } catch (error) {
      console.error('Employee modules and exams API error:', error)
      throw error
    }
  },

  /**
   * Get employee's assignments with detailed module information
   */
  getMyAssignments: async (year?: number): Promise<MyAssignments> => {
    try {
      const params = year ? { year } : {}
      const response = await api.get<ApiResponse<MyAssignments>>('/my-assignments', { params })
      return response.data.data
    } catch (error) {
      console.error('Employee assignments API error:', error)
      throw error
    }
  },

  /**
   * Get employee's accessible modules (current month + completed modules)
   */
  getMyAccessibleModules: async (year?: number): Promise<AccessibleModules> => {
    try {
      const params = year ? { year } : {}
      const response = await api.get<ApiResponse<AccessibleModules>>('/my-accessible-modules', { params })
      return response.data.data
    } catch (error) {
      console.error('Employee accessible modules API error:', error)
      throw error
    }
  },

  /**
   * Get module preview for accessible modules (Employee only)
   */
  getMyModulePreview: async (moduleId: number): Promise<{
    module_id: number
    module_name: string
    filename: string
    file_extension: string
    file_size: number
    file_size_formatted: string
    mime_type: string
    file_url: string | null
    is_previewable: boolean
    preview_type: string
    access_type: string
  }> => {
    try {
      const response = await api.get<ApiResponse<{
        module_id: number
        module_name: string
        filename: string
        file_extension: string
        file_size: number
        file_size_formatted: string
        mime_type: string
        file_url: string | null
        is_previewable: boolean
        preview_type: string
        access_type: string
      }>>(`/my-module-preview/${moduleId}`)
      return response.data.data
    } catch (error) {
      console.error('Employee module preview API error:', error)
      throw error
    }
  },

  /**
   * Download module file for accessible modules (Employee only)
   */
  downloadMyModuleFile: async (moduleId: number): Promise<Blob> => {
    try {
      // Add cache-busting timestamp to prevent browser caching
      const timestamp = new Date().getTime()
      const response = await api.get(`/my-module-preview/${moduleId}/download?t=${timestamp}`, {
        responseType: 'blob',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      })
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        // If error response is a blob, convert it to JSON
        if (error.response.data instanceof Blob) {
          const text = await error.response.data.text()
          const errorData = JSON.parse(text)
          throw errorData
        }
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      }
    }
  }
}